
import React from 'react';

import {
    ACTION_SHORTCUT_TRIGGERED,
    AUDIO_MUTE,
    createShortcutEvent,
    sendAnalytics
} from '../../analytics';
import { AUDIO_MUTE_BUTTON_ENABLED, getFeatureFlag } from '../../base/flags';
import { translate } from '../../base/i18n';
import { MHTrackEvents } from '../../base/lib-meet-hour';
import { MEDIA_TYPE } from '../../base/media';
import { isLocalParticipantModerator } from '../../base/participants';
import { connect } from '../../base/redux';
import { getBrandingColorPalette } from '../../base/styles';
import { AbstractAudioMuteButton, ToolboxItem } from '../../base/toolbox/components';
import type { AbstractButtonProps } from '../../base/toolbox/components';
import { getLocalAudioTrack, getLocalMHAudioTrack, isLocalTrackMuted } from '../../base/tracks';
import { muteLocal } from '../../video-menu/actions';

declare var APP: Object;
declare var interfaceConfig: Object;

/**
 * Converts a color string (hex, rgb, rgba) to rgba with the specified alpha.
 *
 * @param {string} colorStr - The color string.
 * @param {number} alpha - The alpha value (0 to 1).
 * @returns {string} - The rgba string.
 */
function toRgba(colorStr: string, alpha: number): string {
    if (!colorStr) {
        return `rgba(26, 204, 141, ${alpha})`;
    }

    const trimmed = colorStr.trim();

    if (trimmed.startsWith('#')) {
        let hex = trimmed.slice(1);

        if (hex.length === 3) {
            hex = hex
                .split('')
                .map(c => c + c)
                .join('');
        }
        if (hex.length >= 6) {
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);

            if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            }
        }
    }

    const rgbMatch = trimmed.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);

    if (rgbMatch) {
        return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${alpha})`;
    }

    const rgbaMatch = trimmed.match(/^rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)$/i);

    if (rgbaMatch) {
        return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`;
    }

    return `rgba(26, 204, 141, ${alpha})`;
}

/**
 * The type of the React {@code Component} props of {@link AudioMuteButton}.
 */
type Props = AbstractButtonProps & {

    /**
     * Whether audio is currently muted or not.
     */
    _audioMuted: boolean,

    /**
     * Disablles audio button.
     */
     _lockAudio: boolean,

    /**
     * Color to use when participant is speaking.
     */
    _speakingColor: string,

    /**
     * Whether the button is disabled.
     */
    _disabled: boolean,

    /**
     * The local audio track.
     */
    _audioTrack: Object,

    /**
     * The underlying MeetHour audio track.
     */
    _mhTrack: Object,

    /**
     * The redux {@code dispatch} function.
     */
    dispatch: Function
}

type State = {
    audioLevel: number,
    isSpeaking: boolean
};

/**
 * Component that renders a toolbar button for toggling audio mute.
 *
 * @augments AbstractAudioMuteButton
 */
class AudioMuteButton extends AbstractAudioMuteButton<Props, State> {
    accessibilityLabel = 'toolbar.accessibilityLabel.mute';
    label = 'toolbar.mute';

    _speakingTimeout: any;

    /**
     * Initializes a new {@code AudioMuteButton} instance.
     *
     * @param {Props} props - The read-only React {@code Component} props with
     * which the new instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            audioLevel: 0,
            isSpeaking: false
        };

        this._speakingTimeout = null;

        // Bind event handlers so they are only bound once per instance.
        this._onKeyboardShortcut = this._onKeyboardShortcut.bind(this);
        this._updateAudioLevel = this._updateAudioLevel.bind(this);
    }

    /**
     * Registers the keyboard shortcut that toggles the audio muting.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        typeof APP === 'undefined'
            || APP.keyboardshortcut.registerShortcut(
                'M',
                null,
                this._onKeyboardShortcut,
                'keyboardShortcuts.mute');

        this._listenForAudioUpdates();
    }

    /**
     * Handles track and mute changes to keep audio listener in sync.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidUpdate(prevProps: Props) {
        const prevTrack = prevProps._audioTrack?.mhTrack || prevProps._mhTrack;
        const currentTrack = this.props._audioTrack?.mhTrack || this.props._mhTrack;

        if (prevTrack !== currentTrack) {
            this._stopListeningForAudioUpdates(prevTrack);
            this._listenForAudioUpdates();
            this._updateAudioLevel(0);
        } else if (
            (prevProps._audioMuted !== this.props._audioMuted && this.props._audioMuted)
            || (prevProps._audioTrack?.muted !== this.props._audioTrack?.muted && this.props._audioTrack?.muted)
        ) {
            this._updateAudioLevel(0);
        }
    }

    /**
     * Unregisters the keyboard shortcut and stops audio updates.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        typeof APP === 'undefined'
            || APP.keyboardshortcut.unregisterShortcut('M');

        const track = this.props._audioTrack?.mhTrack || this.props._mhTrack;

        this._stopListeningForAudioUpdates(track);
        if (this._speakingTimeout) {
            clearTimeout(this._speakingTimeout);
            this._speakingTimeout = null;
        }
    }

    /**
     * Starts listening for audio level changes.
     *
     * @private
     * @returns {void}
     */
    _listenForAudioUpdates() {
        const mhTrack = this.props._audioTrack?.mhTrack || this.props._mhTrack;

        if (mhTrack && typeof mhTrack.on === 'function') {
            mhTrack.on(
                MHTrackEvents.TRACK_AUDIO_LEVEL_CHANGED,
                this._updateAudioLevel
            );
        }
    }

    /**
     * Stops listening for audio level changes.
     *
     * @param {Object} track - The track to stop listening to.
     * @private
     * @returns {void}
     */
    _stopListeningForAudioUpdates(track) {
        const mhTrack = track?.mhTrack || track;

        if (mhTrack && typeof mhTrack.off === 'function') {
            mhTrack.off(
                MHTrackEvents.TRACK_AUDIO_LEVEL_CHANGED,
                this._updateAudioLevel
            );
        }
    }

    /**
     * Updates internal audio level and speaking state.
     *
     * @param {number} audioLevel - The new audio level.
     * @private
     * @returns {void}
     */
    _updateAudioLevel(audioLevel) {
        const isMuted = this._isAudioMuted()
            || Boolean(this.props._lockAudio)
            || Boolean(this.props._audioTrack?.muted);

        if (isMuted) {
            if (this._speakingTimeout) {
                clearTimeout(this._speakingTimeout);
                this._speakingTimeout = null;
            }
            if (this.state.audioLevel !== 0 || this.state.isSpeaking) {
                this.setState({
                    audioLevel: 0,
                    isSpeaking: false
                });
            }

            return;
        }

        const rawLevel = typeof audioLevel === 'number' && !isNaN(audioLevel)
            ? Math.max(0, Math.min(audioLevel, 1))
            : 0;

        // Apply the same sensitivity scaling as DisplayName's AudioLevelIndicator
        const sensitiveLevel = rawLevel > 0 ? Math.min(rawLevel * 2.5, 1) : 0;

        // Threshold corresponding to DisplayName's active bar (1 / 5 = 0.2)
        const isSpeaking = sensitiveLevel >= 0.2;

        if (isSpeaking) {
            if (this._speakingTimeout) {
                clearTimeout(this._speakingTimeout);
            }

            // Gentle hangover to bridge momentary dips between words without abruptly flickering
            this._speakingTimeout = setTimeout(() => {
                this.setState({
                    isSpeaking: false,
                    audioLevel: 0
                });
            }, 250);

            this.setState({
                audioLevel: sensitiveLevel,
                isSpeaking: true
            });
        } else if (this.state.isSpeaking) {
            // Syllable momentary dip: update audioLevel continuously so the glow
            // smoothly tracks the voice in real-time, matching DisplayName's fluctuating bars
            this.setState({
                audioLevel: sensitiveLevel
            });
        } else if (this.state.audioLevel !== 0) {
            this.setState({
                audioLevel: 0
            });
        }
    }

    /**
     * Indicates if audio is currently muted ot nor.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isAudioMuted() {
        return this.props._audioMuted;
    }

    _onKeyboardShortcut: () => void;

    /**
     * Creates an analytics keyboard shortcut event and dispatches an action to
     * toggle the audio muting.
     *
     * @private
     * @returns {void}
     */
    _onKeyboardShortcut() {
        if (this.props._lockAudio) {
            return;
        }
        sendAnalytics(
            createShortcutEvent(
                AUDIO_MUTE,
                ACTION_SHORTCUT_TRIGGERED,
                { enable: !this._isAudioMuted() }));

        super._handleClick();
    }

    /**
     * Changes the muted state.
     *
     * @param {boolean} audioMuted - Whether audio should be muted or not.
     * @protected
     * @returns {void}
     */
    _setAudioMuted(audioMuted: boolean) {
        this.props.dispatch(muteLocal(audioMuted, MEDIA_TYPE.AUDIO));
    }

    /**
     * Return a boolean value indicating if this button is disabled or not.
     *
     * @returns {boolean}
     */
    _isDisabled() {
        return Boolean(this.props._disabled) || this.props._lockAudio;
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {React$Node}
     */
    render(): React$Node {
        const { isSpeaking, audioLevel } = this.state;
        const isActuallySpeaking = isSpeaking && !this._isAudioMuted() && !this.props._lockAudio;
        const levelPercent = Math.round((audioLevel || 0) * 100);
        const greenColor = this.props._speakingColor || 'rgb(26, 204, 141)';

        // Color based on audio level intensity, matching AudioLevelIndicator.js:
        // audioLevel === 0: muted/no audio
        // audioLevel < 0.8: #F59E0B (Yellow for medium)
        // audioLevel >= 0.8: greenColor (Green for high)
        let speakingColor = '#6B7280';

        if (isActuallySpeaking && audioLevel > 0) {
            speakingColor = audioLevel < 0.8 ? '#F59E0B' : greenColor;
        }

        const speakingGlow = toRgba(speakingColor, 0.65);

        const speakingClass = isActuallySpeaking ? 'is-speaking' : '';
        const combinedClass = [ 'microphone-toolbox-button', this.props.customClass, speakingClass ]
            .filter(Boolean)
            .join(' ');

        const dynamicStyles = {
            '--mic-speaking-color': speakingColor,
            '--mic-speaking-glow': speakingGlow,
            '--audio-level': `${isActuallySpeaking ? levelPercent : 0}%`,
            '--audio-level-num': isActuallySpeaking ? audioLevel : 0
        };

        const props = {
            ...this.props,
            accessibilityLabel: this.accessibilityLabel,
            customClass: combinedClass,
            disabled: this._isDisabled(),
            elementAfter: this._getElementAfter(),
            icon: this._getIcon(),
            label: this._getLabel(),
            styles: this._getStyles(),
            toggled: this._isToggled(),
            tooltip: this._getTooltip(),
            extraStyles: dynamicStyles
        };

        return (
            <ToolboxItem
                disabled = { this._isDisabled() }
                onClick = { this._onClick }
                { ...props } />
        );
    }
}

/**
 * Maps (parts of) the redux state to the associated props for the
 * {@code AudioMuteButton} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
 *     _audioMuted: boolean,
 *     _disabled: boolean
 * }}
 */
function _mapStateToProps(state): Object {
    const _audioTrack = getLocalAudioTrack(state['features/base/tracks']);
    const _mhTrack = getLocalMHAudioTrack(state);
    const _audioMuted = isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.AUDIO);
    const _disabled = state['features/base/config'].startSilent;
    const enabledFlag = getFeatureFlag(state, AUDIO_MUTE_BUTTON_ENABLED, true);
    const { lockAudio } = state['features/base/conference'];
    const isPersonModerator = isLocalParticipantModerator(state);
    const participants = state['features/base/participants'];

    const intConfig = typeof interfaceConfig === 'undefined'
        ? state['features/base/config']?.interfaceConfig
        : interfaceConfig;
    const isBranded = Boolean(intConfig?.applyMeetingSettings);
    const brandedSecondary = isBranded ? getBrandingColorPalette(intConfig)?.secondary : null;
    const speakingColor = (brandedSecondary && brandedSecondary.trim()) || 'rgb(26, 204, 141)';

    let localParticiapnt;
    let lockMyaudio;

    if (navigator.product !== 'ReactNative') {
        localParticiapnt = APP.conference.getMyUserId();
        if (localParticiapnt) {
            lockMyaudio = lockAudio[`${localParticiapnt}`];
        }
    }

    if (navigator.product === 'ReactNative') {
        const myId = participants.filter(i => i.local === true);

        if (Array.isArray(myId)) {
            const id = myId[0].id;


            if (id) {
                lockMyaudio = lockAudio[`${id}`];
            }

        }
    }


    return {
        _audioTrack,
        _mhTrack,
        _audioMuted,
        _disabled,
        _lockAudio: lockMyaudio && !isPersonModerator,
        _speakingColor: speakingColor,
        visible: enabledFlag
    };
}

export default translate(connect(_mapStateToProps)(AudioMuteButton));
